import { useEffect, useRef, useState } from 'react';

const pickFields = (entity, fields) => Object.fromEntries(fields.map((k) => [k, entity?.[k] ?? (Array.isArray(entity?.[k]) ? [] : '')]));

// Debounced auto-save: text fields wait 700ms, pickers save at once. Unsaved keys survive server refreshes.
export function useAutoSave(entity, fields, save) {
  const [draft, setDraft] = useState(() => pickFields(entity, fields));
  const [state, setState] = useState('idle');
  const timer = useRef(null);
  const pending = useRef({});
  const saveRef = useRef(save);
  saveRef.current = save;
  const entityRef = useRef(entity);
  entityRef.current = entity;

  useEffect(() => {
    setDraft((d) => { const next = { ...d }; for (const k of fields) if (!(k in pending.current)) next[k] = entity?.[k] ?? ''; return next; });
  }, [entity, fields]);

  const flush = async (revertOnFail = false) => {
    clearTimeout(timer.current);
    const body = pending.current;
    if (!Object.keys(body).length) return;
    pending.current = {};
    setState('saving');
    const ok = await saveRef.current(body);
    if (!ok && revertOnFail) setDraft((d) => ({ ...d, ...pickFields(entityRef.current, Object.keys(body)) }));
    setState(ok ? 'saved' : 'error');
    setTimeout(() => setState((s) => (s === 'saved' ? 'idle' : s)), 1500);
  };
  useEffect(() => () => { if (Object.keys(pending.current).length) saveRef.current(pending.current); }, []);

  const set = (patch, immediate = false) => {
    setDraft((d) => ({ ...d, ...patch }));
    Object.assign(pending.current, patch);
    clearTimeout(timer.current);
    if (immediate) flush(true); else timer.current = setTimeout(() => flush(false), 700);
  };
  return { draft, set, state, flush };
}

export function SaveState({ state }) {
  if (state === 'idle') return null;
  const text = { saving: 'Saving…', saved: 'Saved', error: 'Not saved — try again' }[state];
  const color = state === 'error' ? 'var(--rose-deep)' : state === 'saved' ? 'var(--sage-deep)' : 'var(--ink-3)';
  return <span className="text-xs font-semibold" style={{ color }} data-testid="ws-save-state">{text}</span>;
}
